'use client';

import { c as _c } from "react/compiler-runtime";
import React from 'react';
import { Tooltip } from 'react-tooltip';
type Props = {
  variant?: 'PRIMARY' | 'SECONDARY' | 'WARNING' | 'DANGER' | 'CLEAR' | 'TRANSPARENT';
  text?: string;
  titleElement?: React.ReactNode;
  type?: 'submit' | 'reset' | 'button' | undefined;
  borderTransparent?: boolean;
  classNames?: string;
  disabled?: boolean;
  loading?: boolean;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  icon?: React.ReactNode;
  id?: string;
  tooltip?: string;
  /** Accessible name for an icon-only button, which has no text to fall back
   *  on. Also becomes the hover title. */
  ariaLabel?: string;
};

/**
 * Padding for the filled/bordered pill variants. Exported because PageBanner's
 * BannerAction has to render at the same height — a banner action row mixes
 * <Button> with BannerAction links, and any difference shows up as one pill
 * standing taller than its neighbours.
 *
 * CLEAR and TRANSPARENT keep their own tighter padding; they are icon-ish
 * affordances, not pills.
 */
export const BUTTON_PILL_PADDING = 'px-6 py-2.5';
const Button = t0 => {
  const $ = _c(26);
  const {
    variant: t1,
    text,
    classNames,
    onClick,
    disabled,
    loading,
    type,
    borderTransparent: t2,
    titleElement,
    icon,
    id,
    tooltip,
    ariaLabel
  } = t0;
  const variant = t1 === undefined ? "PRIMARY" : t1;
  const borderTransparent = t2 === undefined ? true : t2;
  const t3 = text ?? ariaLabel;
  const t4 = `min-h-2 cursor-pointer whitespace-normal break-words rounded-full border border-solid font-emphasis text-textPrimary no-underline outline-none transition-all duration-300 ${borderTransparent ? "border-transparent" : "border-ds-border-standard"} ${variant === "WARNING" ? `bg-yellow-600 ${BUTTON_PILL_PADDING} text-white hover:bg-yellow-700` : variant === "DANGER" ? `bg-destructive ${BUTTON_PILL_PADDING} text-white hover:bg-destructive/90` : variant === "PRIMARY" ? `bg-primary ${BUTTON_PILL_PADDING} text-white hover:bg-indigo-400` : variant === "CLEAR" || variant === "TRANSPARENT" ? "w-auto bg-transparent px-3 py-1.5 hover:bg-background-light" : `border-ds-border-standard bg-background-light ${BUTTON_PILL_PADDING} hover:bg-ds-border-hover`} ${disabled ? "cursor-not-allowed opacity-20 grayscale" : ""} ${classNames}`;
  const t5 = onClick ? onClick : undefined;
  let t6;
  if ($[0] !== loading) {
    t6 = loading && <div className="loader" />;
    $[0] = loading;
    $[1] = t6;
  } else {
    t6 = $[1];
  }
  let t7;
  if ($[2] !== icon || $[3] !== loading) {
    t7 = !loading && icon && <div>{icon}</div>;
    $[2] = icon;
    $[3] = loading;
    $[4] = t7;
  } else {
    t7 = $[4];
  }
  let t8;
  if ($[5] !== loading || $[6] !== titleElement) {
    t8 = !loading && titleElement && <div>{titleElement}</div>;
    $[5] = loading;
    $[6] = titleElement;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  const t9 = !loading && !titleElement && text && text;
  let t10;
  if ($[8] !== t6 || $[9] !== t7 || $[10] !== t8 || $[11] !== t9) {
    t10 = <div className="flex items-center justify-center gap-2">{t6}{t7}{t8}{t9}</div>;
    $[8] = t6;
    $[9] = t7;
    $[10] = t8;
    $[11] = t9;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  let t11;
  if ($[13] !== tooltip) {
    t11 = <Tooltip id={tooltip} />;
    $[13] = tooltip;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  let t12;
  if ($[15] !== ariaLabel || $[16] !== disabled || $[17] !== id || $[18] !== t10 || $[19] !== t11 || $[20] !== t3 || $[21] !== t4 || $[22] !== t5 || $[23] !== tooltip || $[24] !== type) {
    t12 = <button id={id} title={t3} aria-label={ariaLabel} type={type} disabled={disabled} className={t4} onClick={t5} data-tooltip-id={tooltip} data-tooltip-content={tooltip}>{t10}{t11}</button>;
    $[15] = ariaLabel;
    $[16] = disabled;
    $[17] = id;
    $[18] = t10;
    $[19] = t11;
    $[20] = t3;
    $[21] = t4;
    $[22] = t5;
    $[23] = tooltip;
    $[24] = type;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  return t12;
};
export default Button;